// src/pages/Dashboard/components/WelcomeCard.jsx
// STATUS: active | UPDATED: 2026-08-02
// LOG:
//   2026-08-30  Role aware. A manager's first login was being handed a
//               salesperson's onboarding.
//   2026-08-02  New: first-run onboarding. Shows only while the user has zero
//               progress; explains the J13 loop in one glance and points at
//               Step 1. Disappears forever once real progress exists.
//
// The first thing anybody sees, and the only chance to explain what this is.
//
// IT USED TO ASSUME EVERYBODY WAS A SALESPERSON
//
// A sales manager signing in for the first time was told "this is the Roadmap
// to the Sale, thirteen steps" and pointed at Step 1, Meet and Greet. That is
// not their job. Their job is the last box in the loop, the one only they can
// do, and being handed somebody else's onboarding is how a manager decides on
// day one that this product is not for them.
//
// So the loop is the same four boxes for everybody, because it is the same
// method, but the last box changes owner and the words change with the reader.
// A manager sees where they fit in a process that is already running. An owner
// sees what they will be able to tell.

import { useNavigate } from 'react-router-dom'
import { Box, HStack, Text, Flex, Button, Icon } from '@chakra-ui/react'
import { ArrowRight, BookOpen, MessageCircle, ClipboardCheck, PenLine } from 'lucide-react'

const SELF_LOOP = [
  { icon: BookOpen, label: 'Learn', detail: 'the step and its word tracks' },
  { icon: MessageCircle, label: 'Practice', detail: 'with the Coach' },
  { icon: ClipboardCheck, label: 'Check', detail: 'lose one and still pass' },
  { icon: PenLine, label: 'Commit', detail: 'to one behavior on your next customer' },
]

// Same four boxes, same method, read from the other side of the desk. The last
// one is the manager's, and it is the only part of this the software cannot do.
const FLOOR_LOOP = [
  { icon: BookOpen, label: 'They learn', detail: 'the step and its word tracks' },
  { icon: MessageCircle, label: 'They practice', detail: 'with the Coach, on their own time' },
  { icon: ClipboardCheck, label: 'They pass', detail: 'the knowledge check' },
  { icon: PenLine, label: 'You sign off', detail: 'once you have watched them run it' },
]

const COPY = {
  employee: {
    eyebrow: 'Welcome to the Academy',
    title: (n) => `${n ? `${n}, this` : 'This'} is the Roadmap to the Sale.`,
    lede: 'Thirteen steps, one professional process. Every step works the same way:',
    loop: SELF_LOOP,
    cta: 'Start Step 1: Meet & Greet',
    to: '/courses/roadmap/meet-and-greet/',
  },
  manager: {
    eyebrow: 'Welcome to the Academy',
    title: (n) => `${n ? `${n}, this` : 'This'} is where the process gets inspected.`,
    lede:
      'Your salespeople work the thirteen steps on their own. The last box is yours, and it is the only part of this nobody can automate:',
    loop: FLOOR_LOOP,
    cta: 'See my floor',
    to: '/team/',
  },
  owner: {
    eyebrow: 'Welcome to the Academy',
    title: (n) => `${n ? `${n}, this` : 'This'} is how you will know it is working.`,
    lede:
      'Your floor works the thirteen steps and your managers sign them off. You get to see whether that is actually happening:',
    loop: FLOOR_LOOP,
    cta: 'See my store',
    to: '/team/',
  },
}
COPY.admin = COPY.owner

export default function WelcomeCard({ firstName, role }) {
  const navigate = useNavigate()
  const copy = COPY[role] || COPY.employee
  return (
    <Box bg="ink" color="onInk" borderRadius="cardLg" p={{ base: 6, md: 8 }}>
      <Text fontSize="mono-xs" fontFamily="mono" textTransform="uppercase" letterSpacing="0.1em" color="onInkSubtle" mb={3}>
        {copy.eyebrow}
      </Text>
      <Text fontSize="display-md" fontWeight={600} letterSpacing="-0.02em" lineHeight={1.2} mb={2}>
        {copy.title(firstName)}
      </Text>
      <Text fontSize="body" color="onInkMuted" lineHeight={1.65} maxW="480px" mb={6}>
        {copy.lede}
      </Text>

      <HStack
        spacing={0}
        mb={7}
        align="stretch"
        flexWrap={{ base: 'wrap', md: 'nowrap' }}
        gap={{ base: 3, md: 0 }}
      >
        {copy.loop.map((item, i) => (
          <HStack key={item.label} flex={{ md: 1 }} spacing={3} pr={{ md: 4 }} minW={{ base: '45%', md: 0 }}>
            <Flex boxSize="34px" borderRadius="full" bg="onInkFill" align="center" justify="center" flexShrink={0}>
              <Icon as={item.icon} boxSize={4} />
            </Flex>
            <Box>
              <Text fontWeight={600} fontSize="body-sm" lineHeight={1.2}>
                {i + 1}. {item.label}
              </Text>
              <Text fontSize="xs" color="onInkSubtle" lineHeight={1.35}>
                {item.detail}
              </Text>
            </Box>
          </HStack>
        ))}
      </HStack>

      <Button
        bg="card"
        color="ink"
        _hover={{ bg: 'onInkStrong' }}
        _active={{ bg: 'onInkMuted' }}
        rightIcon={<ArrowRight size={16} />}
        onClick={() => navigate(copy.to)}
      >
        {copy.cta}
      </Button>

      {/* Two minutes that make everything after it better. The coach spends
          weeks working out who somebody is from how they answer drills, and
          this tells it on day one. Offered, never forced: somebody who wants to
          get straight into Step 1 should. */}
      <Button
        variant="ghost"
        color="onInkSubtle"
        _hover={{ bg: 'onInkFill', color: 'onInk' }}
        onClick={() => navigate('/coach/?mode=intro')}
        ml={{ base: 0, sm: 2 }}
        mt={{ base: 2, sm: 0 }}
      >
        Two minutes with the coach first
      </Button>
    </Box>
  )
}
